import type { JSX } from "react";
import { useEffect, useState } from "react";

import { Stack } from "@similarweb/ui-components/foundations";

import { ALL_TAB_PREVIEW_ITEM_COUNT } from "app/components/React/QuickSearch/config";
import { quickSearchDataAutomation } from "app/components/React/QuickSearch/dataAutomation";
import { sliceQuickSearchExpandableList } from "app/components/React/QuickSearch/helpers/sliceQuickSearchExpandableList";
import type { QuickSearchCatalogPage } from "app/components/React/QuickSearch/pageData/getQuickSearchCatalogPages";
import { QuickSearchReportRow } from "app/components/React/QuickSearch/QuickSearchReportRow";
import { QuickSearchTabEmptyState } from "app/components/React/QuickSearch/QuickSearchTabEmptyState";
import {
    KEYWORD_TAB_TITLE,
    REPORTS_TAB_TITLE,
    WEBSITE_TAB_TITLE,
} from "app/components/React/SearchAutocompleteWithTabs/texts";
import { translate } from "app/filters/i18nFilter";

import { AllTabCollapsibleSection } from "./AllTabCollapsibleSection";
import { AllTabSectionDivider } from "./AllTabPanelPrimitives";
import { useReportCatalogSearchMatches } from "./useReportCatalogSearchMatches";

type QuickSearchAllTabPanelProps = {
    websiteItems: JSX.Element[];
    keywordItems: JSX.Element[];
    reportItems: JSX.Element[];
    searchQuery: string;
    onCatalogReportNavigate: (page: QuickSearchCatalogPage, state: string) => void;
};

export const QuickSearchAllTabPanel = ({
    websiteItems,
    keywordItems,
    reportItems,
    searchQuery,
    onCatalogReportNavigate,
}: QuickSearchAllTabPanelProps) => {
    const [websitesExpanded, setWebsitesExpanded] = useState(false);
    const [keywordsExpanded, setKeywordsExpanded] = useState(false);
    const [reportsExpanded, setReportsExpanded] = useState(false);

    const { trimmedSearch, isSearching, reportCatalogMatches, hasReportSearchHits } =
        useReportCatalogSearchMatches(searchQuery);

    const hasWebsites = websiteItems.length > 0;
    const hasKeywords = keywordItems.length > 0;
    const hasReports = reportItems.length > 0;

    useEffect(() => {
        setReportsExpanded(false);
    }, [trimmedSearch]);

    const visibleWebsites = sliceQuickSearchExpandableList(websiteItems, websitesExpanded);
    const visibleKeywords = sliceQuickSearchExpandableList(keywordItems, keywordsExpanded);

    const reportsToggleableSearch = reportCatalogMatches.length > ALL_TAB_PREVIEW_ITEM_COUNT;
    const visibleReportSearchRows = sliceQuickSearchExpandableList(reportCatalogMatches, reportsExpanded);

    const visibleReports = sliceQuickSearchExpandableList(reportItems, reportsExpanded);

    const websitesToggleable = websiteItems.length > ALL_TAB_PREVIEW_ITEM_COUNT;
    const keywordsToggleable = keywordItems.length > ALL_TAB_PREVIEW_ITEM_COUNT;
    const reportsToggleable = reportItems.length > ALL_TAB_PREVIEW_ITEM_COUNT;

    const showReportsDefault = !isSearching && hasReports;
    const showReportsSearch = hasReportSearchHits;

    if (!hasWebsites && !hasKeywords && !showReportsDefault && !showReportsSearch) {
        return (
            <QuickSearchTabEmptyState
                dataAutomation={quickSearchDataAutomation.tabEmptyAll}
                searchQuery={searchQuery}
            />
        );
    }

    const showDividerBeforeKeywords = hasWebsites && hasKeywords;
    const showDividerBeforeReports = (showReportsDefault || showReportsSearch) && (hasWebsites || hasKeywords);

    return (
        <Stack
            gap="0"
            width="100%"
            data-automation={quickSearchDataAutomation.allTabPanel}
        >
            {hasWebsites ? (
                <AllTabCollapsibleSection
                    title={translate(WEBSITE_TAB_TITLE)}
                    isToggleable={websitesToggleable}
                    isExpanded={websitesExpanded}
                    onToggleExpanded={() => setWebsitesExpanded((expanded) => !expanded)}
                    toggleDataAutomation={quickSearchDataAutomation.allTabWebsitesToggle}
                    dataAutomation={quickSearchDataAutomation.allTabPanelSection("websites")}
                >
                    {visibleWebsites}
                </AllTabCollapsibleSection>
            ) : null}

            {showDividerBeforeKeywords ? <AllTabSectionDivider /> : null}

            {hasKeywords ? (
                <AllTabCollapsibleSection
                    title={translate(KEYWORD_TAB_TITLE)}
                    isToggleable={keywordsToggleable}
                    isExpanded={keywordsExpanded}
                    onToggleExpanded={() => setKeywordsExpanded((expanded) => !expanded)}
                    toggleDataAutomation={quickSearchDataAutomation.allTabKeywordsToggle}
                    dataAutomation={quickSearchDataAutomation.allTabPanelSection("keywords")}
                >
                    {visibleKeywords}
                </AllTabCollapsibleSection>
            ) : null}

            {showDividerBeforeReports ? <AllTabSectionDivider /> : null}

            {showReportsSearch ? (
                <AllTabCollapsibleSection
                    title={translate(REPORTS_TAB_TITLE)}
                    isToggleable={reportsToggleableSearch}
                    isExpanded={reportsExpanded}
                    onToggleExpanded={() => setReportsExpanded((expanded) => !expanded)}
                    dataAutomation={quickSearchDataAutomation.allTabPanelSection("reports")}
                    toggleDataAutomation={quickSearchDataAutomation.allTabReportsToggle}
                >
                    {visibleReportSearchRows.map((page, index) => (
                        <QuickSearchReportRow
                            key={`all-search-${page.id}`}
                            title={page.title}
                            description={page.breadcrumbs.join(" > ")}
                            summarySnippet={page.summary}
                            highlightQuery={trimmedSearch}
                            href={page.url}
                            isExternal={page.isExternal}
                            isLocked={page.isLocked}
                            dataAutomation={quickSearchDataAutomation.reportRowAllTabSummarySearch(index)}
                            onClick={() => onCatalogReportNavigate(page, page.isLocked ? "locked" : "search_result")}
                        />
                    ))}
                </AllTabCollapsibleSection>
            ) : null}

            {showReportsDefault ? (
                <AllTabCollapsibleSection
                    title={translate(REPORTS_TAB_TITLE)}
                    isToggleable={reportsToggleable}
                    isExpanded={reportsExpanded}
                    dataAutomation={quickSearchDataAutomation.allTabPanelSection("reports")}
                    onToggleExpanded={() => setReportsExpanded((expanded) => !expanded)}
                    toggleDataAutomation={quickSearchDataAutomation.allTabReportsToggle}
                >
                    {visibleReports}
                </AllTabCollapsibleSection>
            ) : null}
        </Stack>
    );
};
